/**
 * PLANILHA DO EXCEL DE VERDADE (.xlsx), feita aqui dentro, sem internet e sem biblioteca.
 *
 * Por que não CSV: o romaneio viaja para o computador do vendedor ou do
 * contador, e um .xlsx abre com duplo clique, com várias abas, com número como
 * número e acento como acento, em qualquer Excel do mundo — sem a dança do
 * ponto e vírgula do CSV.
 *
 * Um .xlsx é um ZIP com alguns arquivos XML dentro. Aqui o ZIP é montado sem
 * compressão (método «armazenado»), que todo leitor de ZIP aceita. Os textos
 * vão direto na célula («inlineStr»), sem tabela de textos compartilhados.
 */

export type Celula = string | number | null
export interface Aba {
  nome: string
  linhas: Celula[][]
  /** largura das colunas em caracteres; sem ela, 14 */
  larguras?: number[]
  /** linhas (base 0) em negrito — cabeçalhos e totais */
  negrito?: number[]
}

const enc = new TextEncoder()

function xml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!)
    // caracteres de controle quebram o arquivo
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
}

function coluna(i: number): string {
  let s = ''
  for (i++; i > 0; i = Math.floor((i - 1) / 26)) s = String.fromCharCode(65 + ((i - 1) % 26)) + s
  return s
}

function folha(a: Aba): string {
  const neg = new Set(a.negrito ?? [])
  const ncol = Math.max(1, ...a.linhas.map((l) => l.length))
  const cols = Array.from({ length: ncol }, (_, i) =>
    `<col min="${i + 1}" max="${i + 1}" width="${a.larguras?.[i] ?? 14}" customWidth="1"/>`).join('')
  const linhas = a.linhas.map((l, r) => {
    const s = neg.has(r) ? ' s="1"' : ''
    const cs = l.map((v, c) => {
      const ref = coluna(c) + (r + 1)
      if (v === null || v === '') return ''
      if (typeof v === 'number') return Number.isFinite(v) ? `<c r="${ref}"${s}><v>${v}</v></c>` : ''
      return `<c r="${ref}" t="inlineStr"${s}><is><t xml:space="preserve">${xml(v)}</t></is></c>`
    }).join('')
    return `<row r="${r + 1}">${cs}</row>`
  }).join('')
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><cols>${cols}</cols><sheetData>${linhas}</sheetData></worksheet>`
}

/** Nome de aba: até 31 caracteres, sem []:*?/\ e sem repetir. */
function nomesDeAba(abas: Aba[]): string[] {
  const usados = new Set<string>()
  return abas.map((a) => {
    let n = a.nome.replace(/[[\]:*?/\\]/g, ' ').slice(0, 31) || 'Planilha'
    for (let k = 2; usados.has(n); k++) n = `${n.slice(0, 28)} ${k}`
    usados.add(n)
    return n
  })
}

export function montarXlsx(abas: Aba[]): Uint8Array<ArrayBuffer> {
  const nomes = nomesDeAba(abas)
  const arquivos: [string, string][] = [
    ['[Content_Types].xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>${abas.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('')}</Types>`],
    ['_rels/.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`],
    ['xl/workbook.xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>${nomes.map((n, i) => `<sheet name="${xml(n)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')}</sheets></workbook>`],
    ['xl/_rels/workbook.xml.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${abas.map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join('')}<Relationship Id="rId${abas.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`],
    ['xl/styles.xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts><fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>`],
    ...abas.map((a, i): [string, string] => [`xl/worksheets/sheet${i + 1}.xml`, folha(a)]),
  ]
  return zip(arquivos.map(([n, c]) => [n, enc.encode(c)]))
}

/* ---------- ZIP sem compressão ---------- */

const TABELA_CRC = (() => {
  const t = new Uint32Array(256)
  for (let n = 0; n < 256; n++) {
    let c = n
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    t[n] = c >>> 0
  }
  return t
})()

function crc32(d: Uint8Array): number {
  let c = 0xffffffff
  for (let i = 0; i < d.length; i++) c = TABELA_CRC[(c ^ d[i]!) & 0xff]! ^ (c >>> 8)
  return (c ^ 0xffffffff) >>> 0
}

function zip(arquivos: [string, Uint8Array][]): Uint8Array<ArrayBuffer> {
  const partes: Uint8Array[] = []
  const central: Uint8Array[] = []
  let deslocamento = 0
  for (const [nome, dados] of arquivos) {
    const n = enc.encode(nome)
    const crc = crc32(dados)
    const local = new Uint8Array(30 + n.length)
    const v = new DataView(local.buffer)
    v.setUint32(0, 0x04034b50, true); v.setUint16(4, 20, true); v.setUint16(6, 0x0800, true)
    v.setUint16(8, 0, true); v.setUint16(10, 0, true); v.setUint16(12, 0x21, true)
    v.setUint32(14, crc, true); v.setUint32(18, dados.length, true); v.setUint32(22, dados.length, true)
    v.setUint16(26, n.length, true); v.setUint16(28, 0, true)
    local.set(n, 30)
    const c = new Uint8Array(46 + n.length)
    const w = new DataView(c.buffer)
    w.setUint32(0, 0x02014b50, true); w.setUint16(4, 20, true); w.setUint16(6, 20, true); w.setUint16(8, 0x0800, true)
    w.setUint16(10, 0, true); w.setUint16(12, 0, true); w.setUint16(14, 0x21, true)
    w.setUint32(16, crc, true); w.setUint32(20, dados.length, true); w.setUint32(24, dados.length, true)
    w.setUint16(28, n.length, true); w.setUint32(42, deslocamento, true)
    c.set(n, 46)
    partes.push(local, dados)
    central.push(c)
    deslocamento += local.length + dados.length
  }
  const tamCentral = central.reduce((s, c) => s + c.length, 0)
  const fim = new Uint8Array(22)
  const e = new DataView(fim.buffer)
  e.setUint32(0, 0x06054b50, true); e.setUint16(8, arquivos.length, true); e.setUint16(10, arquivos.length, true)
  e.setUint32(12, tamCentral, true); e.setUint32(16, deslocamento, true)
  const tudo = new Uint8Array(deslocamento + tamCentral + 22)
  let p = 0
  for (const x of [...partes, ...central, fim]) { tudo.set(x, p); p += x.length }
  return tudo
}

/** «Fazenda Boa Vista» → «Fazenda-Boa-Vista»: nome de arquivo sem acento nem espaço. */
export function nomeDeArquivo(...partes: string[]): string {
  return partes.filter(Boolean).join('-')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^\w-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')
}
